import React from 'react';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { Button, Empty, Progress, Space, Spin, Switch, Tag, Typography } from 'antd';
import { jobStatusMeta, useRelativeTime } from '../utils';
import type { JobStatus, ProjectView } from '../types';
import { UnitTypeBlock } from './UnitTypeBlock';

interface ProjectPanelProps {
  item: ProjectView;
  redoLoading: Record<string, boolean>;
  onRedoFailedTasks: (projectId?: string) => void;
}

export const ProjectPanelHeader: React.FC<{
  item: ProjectView;
  loading?: boolean;
  onRefresh?: () => void;
}> = ({ item, loading, onRefresh }) => {
  const createdTime = useRelativeTime(item.project.createdAt);
  const updatedTime = useRelativeTime(item.project.updatedAt);

  return (
    <div className='project-panel__header' style={{ paddingBottom: 4 }}>
      <div className='project-panel__heading'>
        <Typography.Text strong className='project-panel__name'>
          {item.project.name || 'Untitled Project'}
        </Typography.Text>
        {loading && <Spin size='small' style={{ marginLeft: 8 }} />}
        {item.project.versionName && (
          <Tag className='project-panel__tag'>{item.project.versionName}</Tag>
        )}
        {item.project.customerName && (
          <Tag icon={<ShopOutlined />} className='project-panel__tag project-panel__tag--muted'>
            {item.project.customerName}
          </Tag>
        )}
        <Tag icon={<ApartmentOutlined />} className='project-panel__tag project-panel__tag--muted'>
          {item.unitCount.toLocaleString()} Units
        </Tag>
        <Tag icon={<AppstoreOutlined />} className='project-panel__tag project-panel__tag--muted'>
          {item.closetCounts.total.toLocaleString()} Closets
        </Tag>
        {createdTime && (
          <Typography.Text type='secondary' style={{ fontSize: '12px' }}>
            Created {createdTime}
          </Typography.Text>
        )}
        {updatedTime && (
          <Typography.Text type='secondary' style={{ fontSize: '12px' }}>
            Last Changed {updatedTime}
          </Typography.Text>
        )}
        {onRefresh && (
          <Button
            type='text'
            size='small'
            icon={<ReloadOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onRefresh();
            }}
            style={{ marginLeft: 8 }}
          />
        )}
      </div>
      <Space wrap size={[8, 8]} className='project-panel__statuses'>
        {(Object.keys(jobStatusMeta) as JobStatus[]).map((status) => {
          const meta = jobStatusMeta[status];
          const count = item.closetCounts[status];
          return (
            <Tag
              key={status}
              color={meta.color}
              icon={meta.icon}
              className='project-panel__status-tag'
            >
              {meta.label}: {count.toLocaleString()}
            </Tag>
          );
        })}
        <Tag
          color={item.closetCounts.failed > 0 ? 'volcano' : '#52c41a'}
          icon={
            item.closetCounts.failed > 0 ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />
          }
          className='project-panel__status-tag project-panel__tag--muted'
        >
          Done {item.closetCounts.done.toLocaleString()} /{' '}
          {item.closetCounts.total.toLocaleString()}
        </Tag>
      </Space>
    </div>
  );
};

export const ProjectPanelExtra: React.FC<{ item: ProjectView }> = ({ item }) => (
  <Progress
    type='circle'
    percent={item.progress}
    size={56}
    status={
      item.closetCounts.failed > 0 ? 'exception' : item.progress === 100 ? 'success' : 'active'
    }
  />
);

export const ProjectPanelBody: React.FC<ProjectPanelProps> = ({
  item,
  redoLoading,
  onRedoFailedTasks,
}) => {
  const actionableProjectId = item.project.projectId ?? item.project.id ?? item.project.pk;
  const redoInProgress = actionableProjectId ? Boolean(redoLoading[actionableProjectId]) : false;
  const [hideCompleted, setHideCompleted] = React.useState(true);

  const filteredUnitTypes = hideCompleted
    ? item.unitTypes.filter((typeView) =>
        typeView.unitNumbers.some((unitView) =>
          unitView.closets.some((closet) => closet.state !== 'done'),
        ),
      )
    : item.unitTypes;

  return (
    <div className='project-panel__body'>
      <div className='project-panel__actions'>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => onRedoFailedTasks(actionableProjectId)}
          loading={redoInProgress}
          disabled={!actionableProjectId}
        >
          Redo Failed Tasks
        </Button>
        <Space style={{ marginLeft: 16 }}>
          <Typography.Text>Hide Completed</Typography.Text>
          <Switch checked={hideCompleted} onChange={setHideCompleted} />
        </Space>
      </div>
      {item.unitTypes.length === 0 ? (
        <Empty
          description='No unit or closet data available for this project'
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : filteredUnitTypes.length === 0 ? (
        <Empty
          description={hideCompleted ? 'No active unit types found' : 'No unit types found'}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        filteredUnitTypes.map((typeView) => {
          const projectIdForUnits =
            actionableProjectId ?? item.project.projectId ?? item.project.id ?? item.project.pk;
          return (
            <UnitTypeBlock
              key={typeView.unitType.id ?? typeView.unitType.name}
              typeView={typeView}
              projectIdForUnits={projectIdForUnits || ''}
              postMeasure={item.project.postMeasure}
            />
          );
        })
      )}
    </div>
  );
};
